import React, { useState, useEffect, useCallback } from "react"
import { Outlet, useMatch } from "react-router-dom"
import { AppSidebar } from "./AppSidebar"
import { MobileHeader } from "./MobileHeader"
import { useOrganizationsStore } from "@/stores/organizationsStore"
import { useAuthStore } from "@/stores/authStore"
import { cn } from "@/lib/utils"
import OrganizationSelector from "@/components/OrganizationSelector"
import ImpersonationBanner from "@/components/impersonation/ImpersonationBanner"
import ImpersonationSelector from "@/components/impersonation/ImpersonationSelector"

const SIDEBAR_COLLAPSED_KEY = "sidebar-collapsed"

export function AppLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
    return saved === "true"
  })
  const [orgSelectorOpen, setOrgSelectorOpen] = useState(false)
  const [impersonationOpen, setImpersonationOpen] = useState(false)

  const user = useAuthStore((state) => state.user)
  const { selectedOrganizationId, organizations, fetchOrganizations, setSelectedOrganizationId } = useOrganizationsStore()

  // Fetch organizations when user is available
  useEffect(() => {
    if (user?.organizations) {
      fetchOrganizations(user.organizations)
    }
  }, [user?.organizations, fetchOrganizations])

  // Auto-select organization if only one exists
  useEffect(() => {
    if (organizations.length === 1 && !selectedOrganizationId) {
      setSelectedOrganizationId(organizations[0].id)
    }
  }, [organizations, selectedOrganizationId, setSelectedOrganizationId])

  // Routes that choose the organization themselves from what they load (a
  // deep link to one action item). Asking the user to pick first would be
  // wrong: the item decides, and the page switches to its organization.
  const isClinicOperations = useMatch("/admin/clinic-operations") !== null
  const isClinicDemo = useMatch("/clinic-operations/demo") !== null
  const orgFromLink = useMatch("/action-items/:id") !== null || isClinicOperations || isClinicDemo

  // A remembered selection that is no longer listed (the clinic was archived,
  // or the user left it) is dropped, so the picker opens instead of the app
  // quietly showing a clinic nobody should see.
  useEffect(() => {
    if (
      selectedOrganizationId &&
      organizations.length > 0 &&
      !orgFromLink &&
      !organizations.some((org) => org.id === selectedOrganizationId)
    ) {
      setSelectedOrganizationId(null)
    }
  }, [selectedOrganizationId, organizations, orgFromLink, setSelectedOrganizationId])

  // Show org selector on load if no org selected and multiple orgs available
  useEffect(() => {
    if (!selectedOrganizationId && organizations.length > 1 && !orgFromLink) {
      setOrgSelectorOpen(true)
    }
  }, [selectedOrganizationId, organizations, orgFromLink])

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed))
  }, [sidebarCollapsed])

  const handleOrgSwitcherClick = useCallback(() => {
    setOrgSelectorOpen(true)
  }, [])

  const handleImpersonateClick = useCallback(() => {
    setImpersonationOpen(true)
  }, [])

  return (
    // Column so the impersonation banner spans the full width above the
    // sidebar; the sidebar/main row below owns the remaining height.
    // h-[100dvh] (not h-screen) so iOS Safari's toolbars don't clip the
    // bottom; w-full (not w-screen) so a scrollbar never causes x-overflow.
    <div className="flex h-[100dvh] w-full flex-col overflow-hidden">
      <ImpersonationBanner />

      {/* Phone-only top bar + nav drawer (hidden at md and up) */}
      <MobileHeader
        onOrgClick={handleOrgSwitcherClick}
        onImpersonateClick={handleImpersonateClick}
      />

      <div className="flex min-h-0 min-w-0 flex-1">
        {/* Sidebar (desktop only; hidden below md inside AppSidebar) */}
        <AppSidebar
          collapsed={sidebarCollapsed}
          onCollapsedChange={setSidebarCollapsed}
          onOrgClick={handleOrgSwitcherClick}
          onImpersonateClick={handleImpersonateClick}
        />

        {/* Main Content */}
        <main
          className={cn(
            "min-w-0 flex-1 overflow-y-auto overflow-x-hidden bg-background",
            "transition-all duration-300"
          )}
        >
          <div className="h-full">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Organization Selector (full-screen overlay) */}
      <OrganizationSelector
        open={orgSelectorOpen}
        onOpenChange={setOrgSelectorOpen}
      />

      {/* View-as-user picker (full-screen overlay) */}
      <ImpersonationSelector
        open={impersonationOpen}
        onOpenChange={setImpersonationOpen}
      />
    </div>
  )
}

export default AppLayout
